/**
 * IconButton Component
 * 
 * Standardized icon-only button with accessibility support.
 * 
 * @example
 * ```tsx
 * <IconButton
 *   icon={<Save className="h-4 w-4" />}
 *   label="Save document"
 *   shortcut="⌘S"
 *   onClick={handleSave}
 *   loading={saving}
 *   data-testid={testIds.documentViewer.saveButton}
 * />
 * ```
 */

import { Button } from './button'
import { Loader2 } from 'lucide-react'
import type { ButtonProps } from './button'

interface IconButtonProps extends Omit<ButtonProps, 'children'> {
  icon: React.ReactNode
  label: string
  shortcut?: string
  loading?: boolean
  'data-testid'?: string
}

export function IconButton({
  icon,
  label,
  shortcut,
  variant = 'ghost',
  size = 'icon',
  onClick,
  disabled,
  loading,
  className,
  'data-testid': testId,
  ...props
}: IconButtonProps) {
  const ariaLabel = shortcut ? `${label} (${shortcut})` : label
  const title = shortcut ? `${label} (${shortcut})` : label

  return (
    <Button
      variant={variant}
      size={size}
      onClick={onClick}
      disabled={disabled || loading}
      aria-label={loading ? `${label}...` : ariaLabel}
      aria-busy={loading}
      title={title}
      className={className}
      data-testid={testId}
      {...props}
    >
      {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : icon}
    </Button>
  )
}

